import Phaser from 'phaser';
import { HIGH_SCORE_KEY, HIGH_FLOOR_KEY, loadNumber } from '../storage';
import { tr, getLanguage, setLanguage, LANGUAGES } from '../i18n';
import { FocusNav, FocusItem } from '../ui/FocusNav';

/** 品牌主页：菜单页左上角按钮的跳转目标 */
const HOMEPAGE_URL = 'https://lazzzyboy.com';

/**
 * 主菜单布局（960x540，自上而下分区，互不重叠）
 *
 *   ┌───────────────────────────────┐
 *   │          标题 / 副标题          │  74 / 116
 *   │            最高纪录             │  152
 *   │  ───────────────────────────── │  178 (分隔线)
 *   │   [ 开始游戏 ]  [ 选关卡 ]       │  236  ← 两个行动按钮（左右并排）
 *   │          按空格或点击            │  282
 *   │  ┌─────── 操作说明 ────────┐    │  302..466
 *   │  │  两栏 × 四行               │    │
 *   │  └──────────────────────────┘    │
 *   │             语言                │  486
 *   │   English  简体  繁體  日本語   │  514
 *   └───────────────────────────────┘
 *
 *  键盘：↑↓ 在「开始 / 语言行」之间移动，←→ 在语言之间移动，回车选择。
 *  左上角另有「LazzzyBoy 主页」按钮（仅本菜单页可见，点击在新标签页打开官网）。
 */
export default class MenuScene extends Phaser.Scene {
  private langSwitchAt = 0;
  private starting = false;
  private homeOpenedAt = 0;
  private nav!: FocusNav;

  constructor() {
    super({ key: 'MenuScene' });
  }

  create() {
    const { width } = this.cameras.main;
    const cx = width / 2;
    this.starting = false;

    // ── ① 品牌区 ───────────────────────────────────────────
    this.add.text(cx, 74, 'DUNGEON SHOOTER', {
      fontSize: '46px', fontFamily: 'monospace', color: '#e74c3c',
    }).setOrigin(0.5);

    this.add.text(cx, 116, tr('menu.subtitle'), {
      fontSize: '16px', fontFamily: 'monospace', color: '#95a5a6',
    }).setOrigin(0.5);

    const highScore = loadNumber(HIGH_SCORE_KEY);
    const highFloor = loadNumber(HIGH_FLOOR_KEY);
    this.add.text(cx, 152, tr('menu.best', { score: highScore, floor: highFloor }), {
      fontSize: '18px', fontFamily: 'monospace', color: '#2ecc71',
    }).setOrigin(0.5);

    this.add.rectangle(cx, 178, 420, 2, 0x2c3e50).setOrigin(0.5);

    // ── ①′ 左上角：品牌主页按钮（仅菜单页出现）──────────────
    // 样式与下方「开始游戏 / 选关卡」一致：深蓝底 + 青色描边 + 金色字。
    // 左边缘内缩 40px，为键盘导航的 ▶ 箭头留出显示空间。
    const homeX = 40;
    const homeY = 16;
    const homeW = 180;
    const homeH = 34;
    const homeBtn = this.add.rectangle(homeX, homeY, homeW, homeH, 0x1e2740, 0.95)
      .setOrigin(0, 0)
      .setStrokeStyle(2, 0x66ccff)
      .setInteractive({ useHandCursor: true });
    const homeTxt = this.add.text(homeX + homeW / 2, homeY + homeH / 2, tr('menu.home'), {
      fontSize: '14px', fontFamily: 'monospace', color: '#f1c40f',
    }).setOrigin(0.5);

    // 键盘导航的箭头锚点。FocusNav 把箭头放在 (target.x - displayWidth/2 - 20)。
    // 若直接拿文字当 target，箭头会落在按鈕内部（而且位置随语言文字宽度变化）。
    // 用一个不可见的小块把锚点固定在按钮左边缘外，与语言/文字宽无关。
    const homeAnchor = this.add
      .rectangle(homeX, homeY + homeH / 2, 4, homeH, 0x000000, 0)
      .setVisible(false);

    const focusHome = () => {
      homeBtn.setFillStyle(0x2c3e50, 1);
      homeTxt.setColor('#ffffff');
    };
    const blurHome = () => {
      homeBtn.setFillStyle(0x1e2740, 0.95);
      homeTxt.setColor('#f1c40f');
    };
    homeBtn.on('pointerover', focusHome);
    homeBtn.on('pointerout', blurHome);
    homeBtn.on('pointerdown', (_p: any, _x: any, _y: any, ev: any) => {
      // 阻断全局 pointerdown，否则点这个按钮会顺带开始游戏
      if (ev && ev.stopPropagation) ev.stopPropagation();
      this.openHomepage();
    });

    // ── ② 行动按钮：开始游戏 / 选关卡（左右并排）────────────
    const startY = 236;
    const btnW = 280;
    const btnH = 62;
    const btnOffset = 158; // 两个按钮中心到画面中心的距离

    const startBtn = this.add.rectangle(cx - btnOffset, startY, btnW, btnH, 0x1e2740, 0.95)
      .setStrokeStyle(2, 0x66ccff)
      .setInteractive({ useHandCursor: true });
    const startTxt = this.add.text(cx - btnOffset, startY, tr('menu.startGame'), {
      fontSize: '26px', fontFamily: 'monospace', color: '#f1c40f',
    }).setOrigin(0.5);

    const levelBtn = this.add.rectangle(cx + btnOffset, startY, btnW, btnH, 0x1e2740, 0.95)
      .setStrokeStyle(2, 0x9b59b6)
      .setInteractive({ useHandCursor: true });
    const levelTxt = this.add.text(cx + btnOffset, startY, tr('menu.selectLevel'), {
      fontSize: '26px', fontFamily: 'monospace', color: '#ecf0f1',
    }).setOrigin(0.5);

    this.add.text(cx, startY + 46, tr('menu.startHint'), {
      fontSize: '14px', fontFamily: 'monospace', color: '#7f8c8d',
    }).setOrigin(0.5);

    const focusStart = () => {
      startBtn.setFillStyle(0x2c3e50, 1);
      startTxt.setColor('#ffffff');
    };
    const blurStart = () => {
      startBtn.setFillStyle(0x1e2740, 0.95);
      startTxt.setColor('#f1c40f');
    };
    startBtn.on('pointerover', focusStart);
    startBtn.on('pointerout', blurStart);
    startBtn.on('pointerdown', (_p: any, _x: any, _y: any, ev: any) => {
      if (ev && ev.stopPropagation) ev.stopPropagation();
      this.startGame();
    });

    const focusLevel = () => {
      levelBtn.setFillStyle(0x2c3e50, 1);
      levelTxt.setColor('#ffffff');
    };
    const blurLevel = () => {
      levelBtn.setFillStyle(0x1e2740, 0.95);
      levelTxt.setColor('#ecf0f1');
    };
    levelBtn.on('pointerover', focusLevel);
    levelBtn.on('pointerout', blurLevel);
    levelBtn.on('pointerdown', (_p: any, _x: any, _y: any, ev: any) => {
      if (ev && ev.stopPropagation) ev.stopPropagation();
      this.openLevelSelect();
    });

    // ── ③ 操作面板（两栏，避免纵向拥挤）────────────────────
    const panelW = 700;
    const panelH = 178;
    const panelCy = 382;
    this.add.rectangle(cx, panelCy, panelW, panelH, 0x11131f, 0.65)
      .setStrokeStyle(1, 0x333a4d)
      .setOrigin(0.5);

    this.add.text(cx, panelCy - panelH / 2 + 18, tr('menu.controlsTitle'), {
      fontSize: '14px', fontFamily: 'monospace', color: '#95a5a6',
    }).setOrigin(0.5);

    const controlKeys = ['move', 'shootTap', 'shootHold', 'emptyDir', 'autoFire', 'chooseTalent', 'clearFloor', 'pause', 'menuNav'];
    const colX = [cx - panelW / 2 + 40, cx + 16];
    const rowY0 = panelCy - panelH / 2 + 44;
    const rowGap = 30;
    controlKeys.forEach((k, i) => {
      const col = i < 5 ? 0 : 1;
      const row = i < 5 ? i : i - 5;
      this.add.text(colX[col], rowY0 + row * rowGap, tr(`menu.controls.${k}`), {
        fontSize: '14px', fontFamily: 'monospace', color: '#bdc3c7',
      }).setOrigin(0, 0.5);
    });

    // ── ④ 语言切换（底部居中）──────────────────────────────
    this.add.text(cx, 488, tr('menu.language'), {
      fontSize: '13px', fontFamily: 'monospace', color: '#95a5a6',
    }).setOrigin(0.5);

    const gap = 132;
    const startX = cx - (LANGUAGES.length - 1) * gap / 2;
    const langBtns: Phaser.GameObjects.Text[] = [];
    LANGUAGES.forEach((l, i) => {
      const active = l.code === getLanguage();
      const btn = this.add.text(startX + i * gap, 514, l.label, {
        fontSize: '15px',
        fontFamily: 'monospace',
        color: active ? '#f1c40f' : '#7f8c8d',
        backgroundColor: active ? '#2c3e50' : '#171923',
        padding: { x: 10, y: 5 },
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });

      btn.on('pointerover', () => btn.setColor('#ecf0f1'));
      btn.on('pointerout', () => btn.setColor(active ? '#f1c40f' : '#7f8c8d'));
      btn.on('pointerdown', (_p: any, _x: any, _y: any, ev: any) => {
        if (ev && ev.stopPropagation) ev.stopPropagation();
        this.selectLanguage(l.code);
      });
      langBtns.push(btn);
    });

    // ── ⑤ 键盘焦点导航（上下左右 + 回车）──────────────────
    this.nav = new FocusNav(this, 1, 5);
    const items: FocusItem[] = [
      {
        row: 0, col: 0, target: startTxt, onSelect: () => this.startGame(),
        onFocus: focusStart, onBlur: blurStart,
      },
      {
        row: 0, col: 1, target: levelTxt, onSelect: () => this.openLevelSelect(),
        onFocus: focusLevel, onBlur: blurLevel,
      },
    ];
    LANGUAGES.forEach((l, i) => {
      const fallbackColor = l.code === getLanguage() ? '#f1c40f' : '#7f8c8d';
      items.push({
        row: 1, col: i, target: langBtns[i],
        onSelect: () => this.selectLanguage(l.code),
        onFocus: () => langBtns[i].setColor('#ecf0f1'),
        onBlur: () => langBtns[i].setColor(fallbackColor),
      });
    });
    // 品牌主页按钮放在「开始游戏」上方一行（row -1）：从主行动行按 ↑ 即可到达。
    // 放在数组末尾，保证默认焦点仍是「开始游戏」（setItems 的 startIndex = 0）。
    items.push({
      row: -1, col: 0, target: homeAnchor, onSelect: () => this.openHomepage(),
      onFocus: focusHome, onBlur: blurHome,
    });
    this.nav.setItems(items, 0);

    // ── ⑥ 输入绑定 ─────────────────────────────────────────
    const kb = this.input.keyboard!;
    kb.on('keydown-UP', () => this.nav.move(0, -1));
    kb.on('keydown-DOWN', () => this.nav.move(0, 1));
    kb.on('keydown-LEFT', () => this.nav.move(-1, 0));
    kb.on('keydown-RIGHT', () => this.nav.move(1, 0));
    kb.on('keydown-ENTER', () => this.nav.select());
    kb.on('keydown-SPACE', () => this.startGame());

    const openedAt = this.time.now;
    this.input.on('pointerdown', () => {
      if (this.time.now - openedAt < 200) return;
      if (this.time.now - this.langSwitchAt < 250) return;
      this.startGame();
    });
  }

  private selectLanguage(code: string) {
    this.langSwitchAt = this.time.now;
    setLanguage(code);
    this.scene.restart();
  }

  /** 进入游戏（去重，避免按钮与全局点击同时触发） */
  private startGame() {
    if (this.starting) return;
    this.starting = true;
    this.scene.start('GameScene');
  }

  /** 进入选关界面（仅可选择已通关的关卡） */
  private openLevelSelect() {
    if (this.starting) return;
    this.starting = true;
    this.scene.start('LevelSelectScene');
  }

  /** 在新标签页打开品牌官网（去抖，避免双击开出多个标签） */
  private openHomepage() {
    if (this.time.now - this.homeOpenedAt < 600) return;
    this.homeOpenedAt = this.time.now;
    if (typeof window !== 'undefined') {
      window.open(HOMEPAGE_URL, '_blank', 'noopener,noreferrer');
    }
  }
}
